import axios from "axios";
import tokenManager from "./TokenManager";
import { metrics } from "../metrics/client";
import { getApiBaseUrl } from "../utils/api-config";

// Create an Axios instance
const axiosInstance = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: 300000,
  headers: {
    "Content-Type": "application/json",
  },
});

// ---------------------------------------------------------------------------
// Request interceptor — attaches a fresh token to every outgoing request
// ---------------------------------------------------------------------------
// Uses TokenManager.getToken() which:
//   1. Returns the cached token instantly if it's still valid
//   2. Performs a silent refresh if the token is near expiry
//   3. Never opens a popup or redirect
//   4. Returns null only if all retry attempts fail
axiosInstance.interceptors.request.use(
  async (config) => {
    // Skip token acquisition when auth is bypassed
    if (process.env.REACT_APP_BYPASS_AUTH === 'true') {
      return config;
    }

    try {
      const token = await tokenManager.getToken();
      if (token) {
        config.headers["x-auth-token"] = token;
      } else {
        console.warn("[Axios Interceptor] TokenManager returned no token — request will go out without auth header");
      }
    } catch (error) {
      console.error("[Axios Interceptor] Error acquiring token:", error);
      // Let the request proceed — the server will return 401 and the
      // response interceptor will handle it
    }
    // Stamp request start time for API call duration tracking
    config._requestStartTime = Date.now();

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ---------------------------------------------------------------------------
// Response interceptor — emits clickstream api_call events for every request
// ---------------------------------------------------------------------------
// Tracks method, endpoint, status code, and duration for all API calls.
// Metrics requests are excluded via _isMetricsRequest to prevent infinite
// recursion (api_call event → metrics POST → api_call event → ...).
axiosInstance.interceptors.response.use(
  (response) => {
    if (!response.config._isMetricsRequest) {
      const durationMs = Date.now() - (response.config._requestStartTime || Date.now());
      metrics.add(metrics.apiCallEvent({
        method: response.config.method?.toUpperCase(),
        endpoint: response.config.url,
        statusCode: response.status,
        durationMs,
      }));
    }
    return response;
  },
  (error) => {
    if (error.config && !error.config._isMetricsRequest) {
      const durationMs = Date.now() - (error.config._requestStartTime || Date.now());
      metrics.add(metrics.apiCallEvent({
        method: error.config.method?.toUpperCase(),
        endpoint: error.config.url,
        statusCode: error.response?.status || 0,
        durationMs,
      }));
    }
    return Promise.reject(error);
  }
);

// ---------------------------------------------------------------------------
// Response interceptor — handles 401 with one silent retry
// ---------------------------------------------------------------------------
// If a request gets a 401 (token expired between acquisition and server
// validation, or server-side key rotation), we force-refresh the token once
// and retry the original request.  No popup, no redirect, no user disruption.
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Skip token refresh when auth is bypassed
    if (process.env.REACT_APP_BYPASS_AUTH === 'true') {
      return Promise.reject(error);
    }

    // Skip auth retry for metrics requests to prevent circular telemetry cascade.
    // If the clickstream endpoint returns 401, we just let it fail silently —
    // emitting auth error telemetry for a metrics 401 would queue more events
    // that would also 401, creating an infinite loop.
    if (originalRequest._isMetricsRequest) {
      return Promise.reject(error);
    }

    // Only retry once per request to avoid infinite loops
    if (error.response?.status === 401 && !originalRequest._tokenRetry) {
      originalRequest._tokenRetry = true;

      console.log("[Axios Interceptor] 401 received — attempting silent token refresh and retry");

      try {
        const freshToken = await tokenManager.getToken({ forceRefresh: true });

        if (freshToken) {
          originalRequest.headers["x-auth-token"] = freshToken;
          return axiosInstance(originalRequest);
        }

        // Token refresh failed — emit event for UI + telemetry to handle
        // NOTE: Telemetry is handled by MetricsProvider.jsx listening for
        // the token:auth-failure custom event. Do NOT emit metrics.add() here
        // to avoid duplicate auth error events.
        console.error("[Axios Interceptor] Token refresh failed — cannot retry request");
        window.dispatchEvent(new CustomEvent('token:auth-failure', {
          detail: { url: originalRequest.url, status: 401 }
        }));
      } catch (refreshError) {
        console.error("[Axios Interceptor] Token refresh threw:", refreshError);
        // NOTE: Telemetry is handled by MetricsProvider.jsx listening for
        // the token:auth-failure custom event. Do NOT emit metrics.add() here
        // to avoid duplicate auth error events.
        window.dispatchEvent(new CustomEvent('token:auth-failure', {
          detail: { url: originalRequest.url, status: 401, error: refreshError?.message }
        }));
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
